import { Directive, ElementRef, Input } from '@angular/core';
import { stringStartsWith, runtimeEventManager } from '@wm/core';
import { PageWsService } from '@wms/workspace';
import { find, forEach, get, isEmpty, isUndefined, uniq } from 'lodash-es';
import { ProjectService, PrefabService } from '@wms/services';
import { BaseVariableService } from '@wms/variables';
import { BaseComponent } from '@wms/components';
import { splitExpression } from '@wms/core';
import { getWidgetRegistry } from '@wm/components-base';

@Directive({
  selector: '[wmsExpressionError]',
  standalone: true
})
export class WmsExpressionErrorDirective extends BaseComponent {

  @Input() expression!: any;
  prefabConfig: any;

  constructor(private pageWsService: PageWsService, private baseVariableService: BaseVariableService, private prefabService: PrefabService, private el: ElementRef) {
    super();
    this.registerDestroyListener.add(runtimeEventManager.on('check-expression-existence').subscribe(() => {
      this.checkExprError();
    }));
  }

  ngOnChanges() {
    this.checkExprError();
  }

  /**checks if the expression is valid or not
   * @param bindExpression, bindExpression the expression bound to widget property
   * @errorObj contains all the errors
   * @returns object, contains errorslist**/
  isValidExpression(bindExpression: any, errorObj: any) {
    let list = bindExpression.split(".");
    let bindValToReplace = (this.projectService.isPrefabType() && get(this.pageWsService.activePageInfo, 'type') === 'PARTIAL') ? 'Prefab.' : '';
    if (stringStartsWith(bindExpression, 'Variables')) {
      let variableName = list[1];
      if (!this.baseVariableService.isExists(variableName)) {
        errorObj.Variables.push(variableName);
      }
    } else if (stringStartsWith(bindExpression, 'Widgets')) {
      let widgetName = list[1];
      if (!this.isWidgetExists(widgetName)) {
        errorObj.Widgets.push(widgetName);
      }
    } else if (stringStartsWith(bindExpression, bindValToReplace + 'appLocale')) {
      let localeName = bindValToReplace ? list[2] : list[1];
      if (!this.i18nService.appLocale[localeName]) {
        errorObj.appLocale.push(localeName);
      }
    } else if (stringStartsWith(bindExpression, 'pageParams')) {
      let pageParamName = list[1];
      let pageInfo = this.projectService.getPageInfo(this.pageWsService.activePageName);
      let pageParams = pageInfo.params;
      if (pageParams && isUndefined(find(pageParams, { name: pageParamName }))) {
        errorObj.pageParams.push(pageParamName);
      }
    } else if (this.projectService.isPrefabType()) {
      // if we are inside a partial inside a prefab, then property name will be Prefab.propName, so use list[1] to access prop.
      let properties = get(this.prefabConfig, 'properties'),
        propertyName = (get(this.pageWsService.activePageInfo, 'type') === 'PARTIAL' && list.length > 1) ? list[1] : list[0],
        prefabProperty = get(properties, propertyName);

      if (isUndefined(prefabProperty) && (propertyName !== 'notification' || (propertyName === 'notification' && isUndefined(list[1])))) {
        errorObj.prefabProperties.push(propertyName);
      }
    }
    return errorObj;
  }

  /*
 * function to check if a bound component in given expression exists
 * {param} bindExpression the expression bound to widget property
 */
  bindComponentExists(bindExpression: any) {
    let expressionArray = [],
      isValid = true,
      errorObj: any = {},
      errorTitle,
      expression;
    errorObj.Variables = [];
    errorObj.Widgets = [];
    errorObj.appLocale = [];
    errorObj.pageParams = [];
    errorObj.prefabProperties = [];
    expression = bindExpression && bindExpression.replace('bind:', '');
    expressionArray = splitExpression(expression);
    forEach(expressionArray, (expression: any) => {
      expression = expression.trim();
      errorObj = this.isValidExpression(expression, errorObj);
    });
    isValid = isEmpty(errorObj.Variables) && isEmpty(errorObj.Widgets) && isEmpty(errorObj.appLocale) && isEmpty(errorObj.pageParams) && isEmpty(errorObj.prefabProperties);
    if (!isValid) {
      errorTitle = (!isEmpty(errorObj.Variables) ? (this.locale.MESSAGE_VARIABLE_DELETED + ': ' + uniq(errorObj.Variables).join(',') + '\n') : '') +
        (!isEmpty(errorObj.Widgets) ? (this.locale.MESSAGE_WIDGET_DELETED + ': ' + uniq(errorObj.Widgets).join(',') + '\n') : '') +
        (!isEmpty(errorObj.appLocale) ? (this.locale.MESSAGE_LOCALE_MESSAGES_DELETED + ': ' + uniq(errorObj.appLocale).join(',') + '\n') : '') +
        (!isEmpty(errorObj.pageParams) ? (this.locale.MESSAGE_PAGE_PARAM_DELETED + ': ' + uniq(errorObj.pageParams).join(',') + '\n') : '') +
        (!isEmpty(errorObj.prefabProperties) ? (this.locale.MESSAGE_PREFAB_PROPERTY_DELETED + ': ' + uniq(errorObj.prefabProperties).join(',') + '\n') : '');
    }
    return {
      'isValid': isValid,
      'errorTitle': errorTitle
    };
  }

  validateExpression() {
    let errorObj = this.bindComponentExists(this.expression);
    if (!errorObj.isValid) {
      if (this.el.nativeElement.getAttribute('errorflag')) {
        //for form fields
        $(this.el.nativeElement).addClass('ng-invalid');
        //this.el.nativeElement.focus();
      } else {
        $(this.el.nativeElement).addClass('error');
      }
      this.el.nativeElement.setAttribute('title', errorObj.errorTitle);
    } else {
      $(this.el.nativeElement).removeClass('error');
      $(this.el.nativeElement).removeClass('ng-invalid');
      this.el.nativeElement.setAttribute('title', this.expression);
    }
  }

  isWidgetExists(widgetName: string) {
    let widgetsList: Map<string, any> = getWidgetRegistry();
    return [...widgetsList].find((widget) => { return (widgetName === widget[1].name)});
  }

  checkExprError() {
    // For Prefab projects, first get the prefab config details and then validate the expression
    if (this.projectService.isPrefabType()) {
      (this.prefabService.getConfig() as Promise<any>).then((config: any) => {
        this.prefabConfig = config;
        this.validateExpression();
      });
    } else {
      this.validateExpression();
    }
  }
}

